import React, { useCallback, MouseEvent, ReactNode, useMemo } from 'react';
import { Button } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { DropdownIndicator } from './DropdownIndicator';
import { labelFromValue } from '@shared/helpers/string.helpers';
import { isObject } from 'lodash';
import { OptionalReadonly } from '@shared/models/type-helpers.model';
import { IconButton, type IconButtonProps } from '@ui/buttons';
import { Tooltip } from '@ui/Tooltip';
import { guards } from '@shared/helpers/typeguards.helpers';
import { Menu } from './menus/Menu';
import { Icon } from './Icon';

export type NodonSelectValue = string | number | boolean | undefined;

export type NodonSelectOption<T extends NodonSelectValue> = {
  label?: string | ReactNode;
  icon?: ReactNode;
  selected?: boolean;
  value: T;
};

interface NodonSelectProps<T extends NodonSelectValue = string> {
  buttonLabel?: string;
  tooltipTitle?: string;
  options: OptionalReadonly<(T | NodonSelectOption<T>)[]>;
  formatOptionLabels?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  parentContainerIsButton?: boolean;
  className?: string;
  buttonStyles?: NonNullable<IconButtonProps['sx']>;
  onChange: (value: T) => void;
}

const NodonSelect = <T extends NodonSelectValue>({
  className,
  buttonLabel,
  tooltipTitle,
  options,
  onChange,
  fullWidth,
  disabled = false,
  buttonStyles,
  parentContainerIsButton,
  formatOptionLabels = true,
}: NodonSelectProps<T>) => {
  const formattedOptions = useMemo(
    () => createSelectOptions(options, formatOptionLabels),
    [options, formatOptionLabels],
  );
  const { classes, cx } = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleButtonClick: React.MouseEventHandler = useCallback(
    (e: MouseEvent<HTMLInputElement>) => setAnchorEl(e.currentTarget),
    [],
  );
  const hasIcons = formattedOptions.some((option) =>
    guards.is.defined(option.icon),
  );
  const handleMenuClose = useCallback(() => setAnchorEl(null), []);
  return (
    <>
      {!buttonLabel ? (
        <Tooltip title={tooltipTitle} showWhenDisabled>
          <IconButton
            category="mui"
            icon={anchorEl ? 'KeyboardArrowUp' : 'KeyboardArrowDown'}
            size={12}
            disabled={disabled}
            sx={buttonStyles}
            onClick={handleButtonClick}
          />
        </Tooltip>
      ) : (
        <Button
          color="secondary"
          disabled={disabled}
          fullWidth={fullWidth}
          sx={buttonStyles}
          component={parentContainerIsButton ? 'span' : 'button'}
          className={cx(className, classes.buttonRoot)}
          endIcon={<DropdownIndicator open={Boolean(anchorEl)} />}
          onClick={handleButtonClick}
        >
          {buttonLabel}
        </Button>
      )}

      <Menu
        open={guards.is.defined(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleMenuClose}
      >
        {formattedOptions.map(({ selected, value, label, icon }) => {
          const checkIcon = selected ? (
            <Icon category="mui" icon="Check" />
          ) : undefined;
          return (
            <Menu.Item
              selected={selected}
              key={String(value)}
              icon={hasIcons ? icon : checkIcon}
              onClick={() => onChange(value)}
            >
              {label}
            </Menu.Item>
          );
        })}
      </Menu>
    </>
  );
};

NodonSelect.displayName = 'NodonSelect';

const useStyles = makeStyles()(() => ({
  buttonRoot: {
    textTransform: 'none',
    minWidth: 48,
  },
}));

/**
 * Create a list of select options with better labels based on values
 * @param options
 * @returns
 */
export const createSelectOptions = <T extends NodonSelectValue>(
  options: OptionalReadonly<(T | NodonSelectOption<T>)[]>,
  formatLabel = true,
): NodonSelectOption<T>[] =>
  options.map((option) => {
    const isObj = isObject(option);
    const value = isObj ? option.value : option;
    const label = (isObj && option.label) || labelFromValue(value, formatLabel);
    const icon = isObj ? option.icon : undefined;
    const selected = isObj ? option.selected : undefined;
    return { label, value, icon, selected };
  });

export default NodonSelect;
